<template>
    <section v-if="shouldRenderSection" class="blogs">
        <h2 id="bond-v2-blogs-title" class="blogs__title">Blogs</h2>
        <p>Stay informed with the latest articles, news, and resources across finance, fixed income and investing.</p>
        <div v-if="isLoading" class="blog-slider blog-slider--skeleton" role="status" aria-label="Loading blogs">
            <div class="blog-slider__viewport">
                <div class="blog-track">
                    <article v-for="item in skeletonCount" :key="`blog-skeleton-${item}`" class="blog-card blog-card--skeleton">
                        <div class="blog-skeleton-image blog-skeleton-shimmer"></div>
                        <div class="blog-card__content">
                            <div class="blog-skeleton-date">
                                <span class="blog-skeleton-calendar blog-skeleton-shimmer"></span>
                                <span class="blog-skeleton-line blog-skeleton-line--date blog-skeleton-shimmer"></span>
                            </div>
                            <div class="blog-skeleton-line blog-skeleton-line--title blog-skeleton-shimmer"></div>
                        </div>
                    </article>
                </div>
            </div>
        </div>
        <div v-if="!isLoading && blogs.length" class="blog-slider" role="region" aria-label="Carousel" @mouseenter="pauseSlider" @mouseleave="startSlider">
            <div class="sr-only" aria-live="polite" aria-atomic="true">
                {{ blogs[activeIndex] ? `Slide ${activeIndex + 1} of ${blogs.length}: ${blogs[activeIndex].title}` : '' }}
            </div>
            <div ref="sliderViewport" class="blog-slider__viewport" @focusin="pauseSlider" @focusout="startSlider" @scroll="updateActiveIndex">
                <div class="blog-track">
                    <NuxtLink
                        v-for="blog in blogs"
                        :key="blog._id || blog.title"
                        class="blog-link"
                        :to="getBlogHref(blog)"
                    >
                        <article class="blog-card">
                            <div class="blog-card__header">
                                <img :src="blog.image" :alt="blog.title" class="blog-card__image" />
                            </div>
                            <div class="blog-card__content">
                                <div class="blog-card__heading">
                                    <span class="calendar-icon" aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                                            <path
                                                d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zm0-13H5V6h14v1z"
                                            />
                                        </svg>
                                    </span>
                                    <span class="blog-card__date-text">{{ formatDate(blog.published_at || blog.createdAt) }}</span>
                                </div>
                                <div class="blog-card__info">
                                    <span class="blog-card__title">{{ truncateTitle(blog.title) }}</span>
                                </div>
                            </div>
                        </article>
                    </NuxtLink>
                </div>
            </div>
            <div v-if="showSliderControls" class="blog-slider__controls" role="group" aria-label="Blog carousel controls">
                <button
                    type="button"
                    class="blog-slider__nav-button"
                    aria-label="Previous blogs"
                    @click="slidePrevious"
                >
                    <i class="mdi mdi-chevron-left" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="blog-slider__nav-button blog-slider__nav-button--play-pause"
                    :aria-label="isSliderPaused ? 'Play blog carousel' : 'Pause blog carousel'"
                    :aria-pressed="isSliderPaused"
                    @click="toggleSliderAutoplay"
                >
                    <i :class="['mdi', isSliderPaused ? 'mdi-play' : 'mdi-pause']" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="blog-slider__nav-button"
                    aria-label="Next blogs"
                    @click="slideNext"
                >
                    <i class="mdi mdi-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import blogService from '@/service/blogService'

interface BlogItem {
    _id?: string
    image: string
    createdAt?: string
    published_at?: string
    title: string
    titleUrl?: string
}

interface BlogListResponse {
    data?: BlogItem[]
    totalPages?: number
}

const blogs = ref<BlogItem[]>([])
const isLoading = ref(true)
const sliderViewport = ref<HTMLElement | null>(null)
const activeIndex = ref(0)
const visibleCardCount = ref(4)
const blogPageLimit = 20
const skeletonCount = 4
let sliderInterval: ReturnType<typeof setInterval> | null = null
const isSliderPaused = ref(false)

const maxSlideIndex = computed(() => Math.max(blogs.value.length - visibleCardCount.value, 0))
const showSliderControls = computed(() => maxSlideIndex.value > 0)
const shouldRenderSection = computed(() => isLoading.value || blogs.value.length > 0)

const getBlogQuery = (page = 1) => {
    const params = new URLSearchParams({
        page: String(page),
        limit: String(blogPageLimit),
        isDeleted: 'false',
        sort: 'createdAt',
        order: 'desc'
    })

    return params.toString()
}

const getBlogHref = (blog: BlogItem) => (
    blog.titleUrl ? `/resources/blogs/${blog.titleUrl}` : '/resources/blogs'
)

const truncateTitle = (title: string) => (title.length > 30 ? `${title.slice(0, 30)}...` : title)

const formatDate = (createdAt?: string) => {
    if (!createdAt) return ''

    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(createdAt)) {
        const [year, month, day] = createdAt.split('-').map(Number)
        const dateOnly = new Date(year, month - 1, day)
        return dateOnly.toLocaleDateString('en-GB', options)
    }

    const date = new Date(createdAt)
    if (Number.isNaN(date.getTime())) return ''

    return date.toLocaleDateString('en-GB', options)
}

const getCardStep = () => {
    const viewport = sliderViewport.value
    const firstCard = viewport?.querySelector<HTMLElement>('.blog-link')

    if (!viewport || !firstCard) return 0

    const styles = globalThis.getComputedStyle(viewport.querySelector('.blog-track') as HTMLElement)
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0

    return firstCard.offsetWidth + gap
}

const updateActiveIndex = () => {
    const viewport = sliderViewport.value
    const step = getCardStep()

    if (!viewport || step <= 0) return

    activeIndex.value = Math.max(0, Math.min(Math.round(viewport.scrollLeft / step), maxSlideIndex.value))
}

const updateVisibleCardCount = () => {
    const viewport = sliderViewport.value
    const step = getCardStep()

    if (!viewport || step <= 0) return

    const track = viewport.querySelector<HTMLElement>('.blog-track')
    const styles = track ? globalThis.getComputedStyle(track) : null
    const gap = Number.parseFloat(styles?.columnGap || styles?.gap || '0') || 0
    const visibleCount = Math.round((viewport.clientWidth + gap) / step)

    visibleCardCount.value = Math.max(1, Math.min(visibleCount, blogs.value.length))
    activeIndex.value = Math.min(activeIndex.value, maxSlideIndex.value)
}

const scrollToIndex = (index: number) => {
    const viewport = sliderViewport.value
    const step = getCardStep()

    if (!viewport || step <= 0) return

    const nextIndex = Math.max(0, Math.min(index, maxSlideIndex.value))
    viewport.scrollTo({
        left: step * nextIndex,
        behavior: 'smooth'
    })
    activeIndex.value = nextIndex
}

const slideNext = () => {
    const viewport = sliderViewport.value

    if (!viewport || maxSlideIndex.value <= 0) return

    const isAtEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 8

    if (isAtEnd) {
        scrollToIndex(0)
        return
    }

    scrollToIndex(activeIndex.value + 1)
}

const slidePrevious = () => {
    if (maxSlideIndex.value <= 0) return

    if (activeIndex.value <= 0) {
        scrollToIndex(maxSlideIndex.value)
        return
    }

    scrollToIndex(activeIndex.value - 1)
}

const prefersReducedMotion = () => (
    globalThis.window !== undefined
    && globalThis.window.matchMedia('(prefers-reduced-motion: reduce)').matches
)

const pauseSlider = () => {
    if (sliderInterval) {
        clearInterval(sliderInterval)
        sliderInterval = null
    }
}

const startSlider = () => {
    pauseSlider()

    if (isSliderPaused.value) return
    if (prefersReducedMotion()) return
    if (maxSlideIndex.value <= 0) return

    sliderInterval = setInterval(slideNext, 2600)
}

const toggleSliderAutoplay = () => {
    isSliderPaused.value = !isSliderPaused.value

    if (isSliderPaused.value) {
        pauseSlider()
        return
    }

    startSlider()
}

const handleSliderResize = () => {
    updateVisibleCardCount()
    scrollToIndex(activeIndex.value)
}

const fetchBlogPage = async (page: number) => {
    try {
        return (await blogService.getAllBlogs(getBlogQuery(page))) as BlogListResponse
    } catch {
        return null
    }
}

const fetchBlogs = async () => {
    try {
        isLoading.value = true
        const firstPage = await fetchBlogPage(1)
        blogs.value = firstPage?.data ?? []
        activeIndex.value = 0
    } catch {
        blogs.value = []
    } finally {
        isLoading.value = false
        await nextTick()
        updateVisibleCardCount()
        scrollToIndex(0)
        startSlider()
    }
}

onMounted(() => {
    fetchBlogs()
    globalThis.window.addEventListener('resize', handleSliderResize)
})

onBeforeUnmount(() => {
    pauseSlider()
    globalThis.window.removeEventListener('resize', handleSliderResize)
})
</script>

<style scoped>
.blogs {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 36px 0 12px;
    text-align: center;
}

.blogs__title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 36px;
    line-height: 1.2;
    font-weight: 800;
}

.blogs > p {
    margin: 8px 0 26px;
    color: var(--bond-v2-secondary, var(--color-secondary-40));
    font-size: 16px;
}

.blog-slider {
    position: relative;
    width: 100%;
    max-width: calc((312px * 4) + (30px * 3));
    margin: 0 auto;
}

.blog-slider__viewport {
    overflow: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
}

.blog-track {
    display: flex;
    gap: 30px;
    text-align: left;
}

.blog-link {
    flex: 0 0 312px;
    max-width: 312px;
    color: inherit;
    text-decoration: none;
    scroll-snap-align: start;
}

.blog-link:focus-visible {
    outline: 2px solid var(--bond-v2-primary, var(--color-primary-40));
    border-radius: 16px;
}

.blog-card {
    width: 100%;
    height: 100%;
    border: 1px solid var(--secondaryText, var(--bond-v2-secondary, var(--color-secondary-40)));
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    cursor: pointer;
}

.blog-card__header {
    display: flex;
    justify-content: center;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    margin: -1px -1px 0;
}

.blog-card__image {
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    object-fit: cover;
    display: block;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
}

.blog-card__content {
    padding: 16px;
}

.blog-card__heading {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    color: var(--primaryText, var(--bond-v2-text, var(--color-text-primary)));
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
}

.calendar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
}

.calendar-icon svg {
    display: block;
}

.blog-card__info {
    min-width: 0;
    color: var(--primaryText, var(--bond-v2-text, var(--color-text-primary)));
    font-size: 16px;
    font-weight: 700;
}

.blog-card__title {
    display: block;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.blog-card__date-text {
    font-weight: 800;
    line-height: 16px;
}

.blog-card--skeleton {
    flex: 0 0 312px;
    max-width: 312px;
    min-height: 265px;
    border-color: #d5dced;
    box-shadow: none;
    cursor: default;
}

.blog-skeleton-image {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
}

.blog-skeleton-date {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
}

.blog-skeleton-calendar {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    flex: 0 0 auto;
}

.blog-skeleton-line {
    display: block;
    height: 12px;
    border-radius: 999px;
}

.blog-skeleton-line--date {
    width: 92px;
}

.blog-skeleton-line--title {
    width: 72%;
    height: 17px;
}

.blog-skeleton-shimmer {
    background: linear-gradient(90deg, #eef3fb 0%, #f8fbff 48%, #eef3fb 100%);
    background-size: 220% 100%;
    animation: blog-shimmer 1.35s ease-in-out infinite;
}

@keyframes blog-shimmer {
    0% {
        background-position: 220% 0;
    }

    100% {
        background-position: -220% 0;
    }
}

.blog-slider__controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 42px;
    z-index: 20;
}

.blog-slider__nav-button {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border: 1px solid rgba(0, 39, 175, 0.18);
    border-radius: 50%;
    background-color: #fff;
    color: var(--bond-v2-primary, var(--color-primary-40));
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 39, 175, 0.08);
}

.blog-slider__nav-button:hover,
.blog-slider__nav-button:focus-visible {
    background-color: var(--bond-v2-primary, var(--color-primary-40));
    border-color: var(--bond-v2-primary, var(--color-primary-40));
    color: #fff;
    box-shadow: 0 4px 10px rgba(0, 39, 175, 0.18);
    transform: translateY(-1px);
}

.blog-slider__nav-button:focus-visible {
    outline: 2px solid var(--bond-v2-primary, var(--color-primary-40));
    outline-offset: 2px;
}

.blog-slider__nav-button i {
    font-size: 22px;
}

@media (prefers-reduced-motion: reduce) {
    .blog-slider__viewport {
        scroll-behavior: auto;
    }

    .blog-skeleton-shimmer {
        animation: none;
    }

    .blog-slider__nav-button {
        transition: none;
    }
}

@media (max-width: 980px) {
    .blog-link,
    .blog-card--skeleton {
        flex-basis: calc((100% - 30px) / 2);
        max-width: none;
    }
}

@media (max-width: 560px) {
    .blogs {
        padding: 28px 16px 8px;
        box-sizing: border-box;
        overflow: hidden;
    }

    .blogs__title {
        font-size: 17px;
        line-height: 22px;
    }

    .blogs > p {
        margin-bottom: 18px;
        font-size: 13px;
        line-height: 20px;
    }

    .blog-track {
        gap: 16px;
    }

    .blog-link,
    .blog-card--skeleton {
        flex-basis: 100%;
        max-width: none;
    }

    .blog-card__content {
        padding: 14px;
    }

    .blog-card__heading {
        font-size: 12px;
    }

    .blog-card__info {
        font-size: 14px;
    }

    .blog-slider__nav-button {
        width: 36px;
        height: 36px;
        min-width: 36px;
    }

    .blog-slider__controls {
        margin-top: 24px;
    }
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
